/**
 * Hot-path-aware drift scoring.
 *
 * The base score (drift/scoring.ts) treats every drifted file equally.
 * Reality: a stale "how to run setup" doc is much worse than a stale architecture
 * diagram, because Claude reads the first one constantly.
 *
 * This module weights drift issues by:
 *   1. Issue type — `command`/`path` issues hurt AI more than `staleness`
 *   2. Hot-path multiplier — files Claude actually queries (from telemetry) count more
 *
 * Falls back to the unweighted score when no telemetry data is available.
 */
import type { DriftIssue, IssueCode } from "../types.js";
import type { FileAggregation } from "../telemetry/query-log.js";

/** How "load-bearing" is each issue type for AI behavior? */
const TYPE_WEIGHT: Partial<Record<IssueCode, number>> = {
  // High: AI immediately fails or hallucinates
  MISSING_PATH: 3,
  DEAD_COMMAND: 3,
  // Medium: AI may use wrong API or version
  DEPENDENCY_MISSING: 2,
  VERSION_MISMATCH: 2,
  CROSS_FILE_CONFLICT: 2,
  WORKSPACE_DEPENDENCY_MISSING: 2,
  WORKSPACE_DEPENDENCY_INVALID: 2,
  // Low: cosmetic / hygiene
  UNDOCUMENTED_SCRIPT: 1,
  STALE_FILE: 1,
  TOOL_CONFIG_OUT_OF_SYNC: 1,
  INDEX_MISSING_ENTRY: 1,
  INDEX_ORPHAN_ENTRY: 1,
  DEAD_EDGE: 1,
  CHECKER_ERROR: 1,
};

const DEFAULT_WEIGHT = 1;

/**
 * Compute a hot-path multiplier for a file based on query frequency.
 * Log-scaled so a file queried 100x doesn't dominate one queried 5x.
 * Range: 1.0 (never queried) to 2.0 (very hot).
 */
function hotPathMultiplier(file: string, byFile: Map<string, FileAggregation>): number {
  const agg = byFile.get(file);
  if (!agg) return 1.0;
  // log2(hits + 1) capped at 1.0 → multiplier in [1.0, 2.0]
  const bonus = Math.min(1.0, Math.log2(agg.hits + 1) / 6);
  return 1.0 + bonus;
}

export interface WeightedScoreResult {
  /** 0–100 weighted score, lower is worse */
  score: number;
  /** Sum of weighted deductions before clamping */
  totalWeight: number;
  /** Whether telemetry was available (true) or fallback was used (false) */
  usedTelemetry: boolean;
}

/**
 * Compute a weighted drift score.
 * If `aggregations` is empty, all hot-path multipliers are 1.0 — the result is
 * still type-weighted but not telemetry-aware.
 */
export function weightedScore(
  issues: DriftIssue[],
  aggregations: FileAggregation[] = []
): WeightedScoreResult {
  const byFile = new Map<string, FileAggregation>();
  for (const a of aggregations) byFile.set(a.file, a);

  let totalWeight = 0;
  for (const issue of issues) {
    const typeWeight = TYPE_WEIGHT[issue.code] ?? DEFAULT_WEIGHT;
    const sevMultiplier = issue.severity === "error" ? 3 : issue.severity === "warning" ? 1 : 0;
    const hot = hotPathMultiplier(issue.file, byFile);
    totalWeight += typeWeight * sevMultiplier * hot;
  }

  // Cap weighted deductions to keep the score in 0-100
  const clamped = Math.min(100, totalWeight);
  const score = Math.max(0, Math.round(100 - clamped));

  return {
    score,
    totalWeight: Math.round(totalWeight * 10) / 10,
    usedTelemetry: aggregations.length > 0,
  };
}
